import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  LinearProgress,
  Tooltip,
  Alert,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  People,
  Lock,
  Assignment,
  PhoneAndroid,
  Group,
  Add,
  Assessment
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { dataverseService } from '../services/dataverseService';
import { handleDataverseLoadError, getDataverseErrorMessage } from '../lib/dataverseErrorHandler';
import { loadDataverseWithDemoFallback } from '../lib/loadDataverseData';
import { getDisplayCustomerName } from '../lib/customerDisplay';
import { getDataverseConnectionInfo } from '../config/dataverse';
import { useBlaalysMode } from '../context/BlaalysModeContext';
import BlaalysConfirmDialog from '../components/BlaalysConfirmDialog';

const StatCard = ({ title, value, icon, color, subtitle, onClick }) => (
  <Card
    className="card-hover fade-in"
    sx={{ cursor: onClick ? 'pointer' : 'default', height: '100%' }}
    onClick={onClick}
  >
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Avatar sx={{ bgcolor: color, mr: 2, flexShrink: 0 }}>
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" component="div" fontWeight="600" noWrap title={title}>
            {title}
          </Typography>
          <Typography variant="h4" component="div" color={color}>
            {value}
          </Typography>
        </Box>
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const QuickActionCard = ({ title, description, icon, color, onClick }) => (
  <Card className="card-hover fade-in" sx={{ cursor: 'pointer' }} onClick={onClick}>
    <CardContent sx={{ textAlign: 'center', p: 3 }}>
      <Avatar sx={{ bgcolor: color, width: 56, height: 56, mx: 'auto', mb: 2 }}>
        {icon}
      </Avatar>
      <Typography variant="h6" component="div" fontWeight="600" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </CardContent>
  </Card>
);

const HomePage = () => {
  const navigate = useNavigate();
  const {
    allowBlaalysOverride,
    loading: blaalysLoading,
    setAllowBlaalysOverride
  } = useBlaalysMode();
  const [stats, setStats] = useState({
    totalResidents: 0,
    totalLocks: 0,
    activeAssignments: 0,
    totalPhones: 0,
    totalAccessGroups: 0
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [blaalysDialogOpen, setBlaalysDialogOpen] = useState(false);
  const [blaalysTargetEnabled, setBlaalysTargetEnabled] = useState(false);
  const [blaalysSubmitting, setBlaalysSubmitting] = useState(false);
  const [blaalysError, setBlaalysError] = useState(null);
  const dataverseInfo = getDataverseConnectionInfo();

  const openBlaalysDialog = (enable) => {
    setBlaalysTargetEnabled(enable);
    setBlaalysError(null);
    setBlaalysDialogOpen(true);
  };

  const handleBlaalysConfirm = async () => {
    setBlaalysSubmitting(true);
    setBlaalysError(null);
    try {
      await setAllowBlaalysOverride(blaalysTargetEnabled);
      setBlaalysDialogOpen(false);
    } catch (error) {
      setBlaalysError(
        getDataverseErrorMessage(error, 'Kunne ikke oppdatere blålysmodus.')
      );
    } finally {
      setBlaalysSubmitting(false);
    }
  };

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoadError(null);
        const [{ data }, phones, accessGroups] = await Promise.all([
          loadDataverseWithDemoFallback(
            () => dataverseService.getAssignmentsData(),
            async () => {
              const { mockApi } = await import('../data/demoData');
              const [residents, locks, assignments] = await Promise.all([
                mockApi.getResidents(),
                mockApi.getLocks(),
                mockApi.getAssignments()
              ]);
              return { residents, locks, assignments };
            }
          ),
          dataverseService.phoneService.getAll().catch(() => []),
          dataverseService.accessGroupService.getAll().catch(() => [])
        ]);
        const { residents, locks, assignments } = data;

        setStats({
          totalResidents: residents.length,
          totalLocks: locks.length,
          activeAssignments: assignments.length,
          totalPhones: (phones || []).length,
          totalAccessGroups: (accessGroups || []).length
        });
      } catch (error) {
        console.error('Error loading stats:', error);
        if (handleDataverseLoadError(error, navigate)) {
          return;
        }
        setLoadError(getDataverseErrorMessage(error));
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [navigate]);

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="600">
            Velkommen til OpenCare
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Administrer innbyggere og låstilordninger effektivt
          </Typography>
        </Box>
        <FormControlLabel
          sx={{ ml: { sm: 'auto' }, alignItems: 'center' }}
          control={
            <Checkbox
              checked={Boolean(allowBlaalysOverride)}
              disabled={blaalysLoading || blaalysSubmitting}
              onChange={(event) => openBlaalysDialog(event.target.checked)}
              color="error"
              inputProps={{ 'aria-label': 'Blålysmodus' }}
            />
          }
          label="Blålysmodus"
        />
      </Box>

      <BlaalysConfirmDialog
        open={blaalysDialogOpen}
        enabling={blaalysTargetEnabled}
        submitting={blaalysSubmitting}
        error={blaalysError}
        onCancel={() => {
          if (!blaalysSubmitting) {
            setBlaalysDialogOpen(false);
            setBlaalysError(null);
          }
        }}
        onConfirm={handleBlaalysConfirm}
      />

      {loadError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {loadError}
        </Alert>
      )}

      {/* Statistics Cards — full-width equal columns (same span as hurtighandlinger) */}
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          mb: 4,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(5, 1fr)'
          }
        }}
      >
        <StatCard
          title="Innbyggere"
          value={stats.totalResidents}
          icon={<People />}
          color="primary.main"
          subtitle="Totalt registrert"
          onClick={() => navigate('/residents')}
        />
        <StatCard
          title="Låser"
          value={stats.totalLocks}
          icon={<Lock />}
          color="success.main"
          subtitle="Totalt i systemet"
          onClick={() => navigate('/locks')}
        />
        <StatCard
          title="Tilordninger"
          value={stats.activeAssignments}
          icon={<Assignment />}
          color="warning.main"
          subtitle="Aktive koblinger"
          onClick={() => navigate('/assignments')}
        />
        <StatCard
          title="Telefoner"
          value={stats.totalPhones}
          icon={<PhoneAndroid />}
          color="info.main"
          subtitle="Aktive telefoner"
          onClick={() => navigate('/phones')}
        />
        <StatCard
          title="Tilgangsgrupper"
          value={stats.totalAccessGroups}
          icon={<Group />}
          color="secondary.main"
          subtitle="Aktive grupper"
          onClick={() => navigate('/access-groups')}
        />
      </Box>

      {/* Quick Actions */}
      <Typography variant="h5" component="h2" gutterBottom fontWeight="600" sx={{ mb: 3 }}>
        Hurtighandlinger
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <QuickActionCard
            title="Legg til Innbygger"
            description="Registrer ny innbygger i systemet"
            icon={<Add />}
            color="primary.main"
            onClick={() => navigate('/residents')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QuickActionCard
            title="Tilordn Låser"
            description="Koble innbyggere til låser"
            icon={<Assignment />}
            color="warning.main"
            onClick={() => navigate('/assignments')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <QuickActionCard
            title="Vis Rapporter"
            description="Se oversikt og analyser"
            icon={<Assessment />}
            color="success.main"
            onClick={() => navigate('/reports')}
          />
        </Grid>
      </Grid>

      {/* System Status — compact horizontal traffic light */}
      <Box
        sx={{
          mt: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          width: '100%'
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          Systemstatus
        </Typography>
        <Tooltip
          arrow
          placement="top"
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="caption" display="block">
                Dataverse: {dataverseInfo.orgName}
              </Typography>
              <Typography variant="caption" display="block">
                Entra ID: Aktiv
              </Typography>
              <Typography variant="caption" display="block">
                Kommune: {getDisplayCustomerName()}
              </Typography>
              {dataverseInfo.usesCustomerOverride && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  Kommune-styrt miljø — deploy: {dataverseInfo.buildTimeUrl}, aktiv:{' '}
                  {dataverseInfo.environmentUrl}
                </Typography>
              )}
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Koblet til {dataverseInfo.environmentUrl}
                {dataverseInfo.customerName ? ` for ${dataverseInfo.customerName}` : ''}.
              </Typography>
              {loadError && (
                <Typography variant="caption" display="block" color="error.light" sx={{ mt: 0.5 }}>
                  Feil: {loadError}
                </Typography>
              )}
            </Box>
          }
        >
          <Box
            aria-label="Systemstatus"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1,
              py: 0.5,
              borderRadius: 999,
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
              cursor: 'default'
            }}
          >
            {['error', 'warning', 'success'].map((level) => {
              const active = loadError ? level === 'error' : level === 'success';
              const color =
                level === 'error'
                  ? 'error.main'
                  : level === 'warning'
                    ? 'warning.main'
                    : 'success.main';
              return (
                <Box
                  key={level}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: color,
                    opacity: active ? 1 : 0.22,
                    boxShadow: active ? `0 0 0 2px rgba(0,0,0,0.06)` : 'none'
                  }}
                />
              );
            })}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default HomePage;
